import { createServerFn } from "@tanstack/react-start";

import { listDomains, updateDomain } from "@/features/identities/server";
import { bavimailPublicBackendJson, requireBavimailBackendJson } from "@/lib/bavimail-api.server";
import {
  mapBillingOverviewToView,
  mapBillingPlansToCatalog,
  mapBillingPlansToView,
  mapDomainToHeaderSettingsView,
  type BillingPlanRecord,
  type BillingStatusRecord,
  type SettingsBillingInvoiceView,
  type StorageUsageRecord,
} from "@/features/settings/view-model";

function passthroughValidator<T>(value: T) {
  return value;
}

export const getSettingsHeaderRetentionView = createServerFn({ method: "GET" }).handler(
  async () => {
    const domains = await listDomains();
    return domains.map(mapDomainToHeaderSettingsView);
  },
);

export const getSettingsUsageOverview = createServerFn({ method: "GET" }).handler(async () => {
  const [status, storage] = await Promise.all([
    requireBavimailBackendJson<BillingStatusRecord>(
      { path: "/billing/status" },
      "Failed to load usage overview.",
    ),
    requireBavimailBackendJson<StorageUsageRecord>(
      { path: "/billing/storage" },
      "Failed to load storage usage.",
    ),
  ]);

  return mapBillingOverviewToView(status, storage);
});

export const getSettingsBillingInvoices = createServerFn({ method: "GET" }).handler(
  async (): Promise<SettingsBillingInvoiceView[]> => [],
);

export const getSettingsBillingPlans = createServerFn({ method: "GET" }).handler(async () => {
  const result = await requireBavimailBackendJson<{ plans: BillingPlanRecord[] }>(
    { path: "/billing/plans" },
    "Failed to load billing plans.",
  );

  return mapBillingPlansToView(result.plans);
});

export const createBillingCheckoutSession = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      planCode: string;
      billingInterval: string;
    }>,
  )
  .handler(async ({ data }) => {
    const result = await requireBavimailBackendJson<{
      payment_url: string | null;
      completed: boolean;
    }>(
      { path: "/billing/checkout", method: "POST", json: { plan_id: data.planCode } },
      "Failed to open Autumn checkout.",
    );

    return { url: result.payment_url ?? "/dashboard/settings?tab=billing" };
  });

export const createBillingPortalSession = createServerFn({ method: "POST" }).handler(async () => {
  return await requireBavimailBackendJson<{ url: string }>(
    { path: "/billing/portal", method: "POST", json: {} },
    "Failed to open Autumn billing portal.",
  );
});

export const createBillingInvoicePayment = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      invoiceId: string;
    }>,
  )
  .handler(async () => {
    throw new Error("Invoice payments now live in Autumn billing portal.");
  });

export const getPricingCatalog = createServerFn({ method: "GET" }).handler(async () => {
  // The plan catalog is public: /pricing is indexable and served to anonymous crawlers, so no
  // session token is minted for it.
  const result = await bavimailPublicBackendJson<{ plans: BillingPlanRecord[] }>({
    path: "/billing/plans",
  });

  if (result.error || !result.data) {
    throw new Error(result.error?.message || "Failed to load pricing catalog.");
  }

  return mapBillingPlansToCatalog(result.data.plans);
});

export const updateSettingsHeaderRetention = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      domainId: string;
      extraRetainedHeaders: string[];
    }>,
  )
  .handler(async ({ data }) => {
    const updated = await updateDomain(data.domainId, {
      extra_retained_headers: data.extraRetainedHeaders,
    });

    return mapDomainToHeaderSettingsView(updated);
  });

export const applySettingsHeaderRetentionToAll = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      extraRetainedHeaders: string[];
    }>,
  )
  .handler(async ({ data }) => {
    const domains = await listDomains();
    const updated = await Promise.all(
      domains
        .filter((domain) => domain.is_active)
        .map(async (domain) => {
          const next = await updateDomain(domain.id, {
            extra_retained_headers: data.extraRetainedHeaders,
          });
          return mapDomainToHeaderSettingsView(next);
        }),
    );

    return updated;
  });
